import React, { useState, useRef } from "react";
import { Store, Copy, ExternalLink, Save, Upload, X } from "lucide-react";
import { ShowSuccessToast, ShowDangerToast } from "../services/toast";
import ApiServices from "../services/ApiServices";

const DEFAULT_AVATAR = "https://thumbs.dreamstime.com/b/default-profile-picture-icon-high-resolution-high-resolution-default-profile-picture-icon-symbolizing-no-display-picture-360167031.jpg";

const StoreProfile = ({ storeId, store, onProfileChange }) => {
  const [profile, setProfile] = useState({
    storeName: store.name || "",
    ownerName: store.owner?.username || "",
    email: store.owner?.email || "",
    profilePicture: store.owner?.profilePicture || DEFAULT_AVATAR,
    address: store.address || "",
    taxId: store.taxId || "",
    contactPhone: store.contactPhone || "",
    contactEmail: store.contactEmail || "",
  });

  const [credentials, setCredentials] = useState({
    storeName: store.name || "",
    ownerName: store.owner?.username || "",
    email: store.owner?.email || "",
    address: store.address || "",
    taxId: store.taxId || "",
    contactPhone: store.contactPhone || "",
    contactEmail: store.contactEmail || "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(store.owner?.profilePicture || null);
  const fileInputRef = useRef(null);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      ShowDangerToast('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      ShowDangerToast('Image must be less than 5MB');
      return;
    }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const donationLink = `https://app.donordispatch.com/donation/${store?.storeId || storeId}`;

  const handleCredentialChange = (e) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    let formatted = '';
    if (value.length > 0) {
      formatted = value.substring(0, 3);
    }
    if (value.length > 3) {
      formatted += '-' + value.substring(3, 6);
    }
    if (value.length > 6) {
      formatted += '-' + value.substring(6, 10);
    }
    setCredentials({ ...credentials, contactPhone: formatted });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!credentials.storeName.trim()) return ShowDangerToast("Store name is required");
    if (!credentials.ownerName.trim()) return ShowDangerToast("Owner name is required");
    if (!credentials.email.trim()) return ShowDangerToast("Email is required");

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(credentials.email))
      return ShowDangerToast("Please enter a valid email address");

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("storeId", storeId);
      if (credentials.storeName !== store.name) formData.append("storeName", credentials.storeName);
      if (credentials.ownerName !== store.owner.username)
        formData.append("ownerName", credentials.ownerName);
      if (credentials.email !== store.owner.email) formData.append("email", credentials.email);
      if (credentials.address !== store.address) formData.append("address", credentials.address);
      if (credentials.taxId !== store.taxId) formData.append("taxId", credentials.taxId);
      if (logoFile) formData.append("ownerProfilePicture", logoFile);

      const response = await ApiServices.postFormData(`/stores/update-profile/${storeId}`, formData);

      const updatedProfile = {
        storeName: response.data.store.name,
        ownerName: response.data.user.username,
        email: response.data.user.email,
        profilePicture: response.data.user.profilePicture,
        address: response.data.store.address,
        taxId: response.data.store.taxId,
        contactPhone: credentials.contactPhone,
        contactEmail: credentials.contactEmail,
      };

      setProfile(updatedProfile);
      setCredentials(updatedProfile);
      setLogoPreview(updatedProfile.profilePicture || null);
      setLogoFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      ShowSuccessToast("Profile updated successfully");

      if (onProfileChange) onProfileChange(updatedProfile);
    } catch (error) {
      ShowDangerToast("Failed to update profile: " + (error.response?.data?.message || "Server error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(donationLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
      <div className="flex items-center gap-2.5 mb-1">
        <Store className="w-5 h-5 text-navy-600" />
        <h3 className="text-base font-semibold text-navy-800">Store Profile</h3>
      </div>
      <p className="text-sm text-gray-500 mb-5 ml-[30px]">Basic information about your store</p>

      <div className="flex items-center gap-5 mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="relative flex-shrink-0">
          <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-gray-200 bg-white">
            {logoPreview ? (
              <img src={logoPreview} alt="Store logo" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-navy-50 text-navy-400">
                <Store className="w-8 h-8" />
              </div>
            )}
          </div>
          {logoPreview && (
            <button
              type="button"
              onClick={handleRemoveLogo}
              className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-700 mb-1">Store Logo</p>
          <p className="text-xs text-gray-500 mb-2.5">Upload your store logo. Recommended size: 200x200px. Max 5MB.</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleLogoChange}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-navy-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Upload className="w-3.5 h-3.5" />
            {logoPreview ? 'Change Logo' : 'Upload Logo'}
          </button>
        </div>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
        <p className="text-sm font-medium text-green-700 mb-2">Public Donation Link</p>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700 bg-white border border-gray-200 rounded-lg px-3 py-2 flex-1 truncate">{donationLink}</span>
          <button
            type="button"
            onClick={handleCopyLink}
            className="p-2 text-gray-500 hover:text-navy-600 hover:bg-white rounded-lg transition-colors border border-gray-200 bg-white"
            title={copied ? "Copied!" : "Copy link"}
          >
            <Copy className="w-4 h-4" />
          </button>
          <a
            href={donationLink}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 text-gray-500 hover:text-navy-600 hover:bg-white rounded-lg transition-colors border border-gray-200 bg-white"
            title="Open link"
          >
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>

      <form onSubmit={handleSubmit} noValidate>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Store Name <span className="text-brand-orange">*</span>
              </label>
              <input
                type="text"
                name="storeName"
                value={credentials.storeName}
                onChange={handleCredentialChange}
                className="input-field"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Store Slug</label>
              <input
                type="text"
                value={store?.storeId || ""}
                className="input-field bg-gray-50"
                disabled
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Owner Name <span className="text-brand-orange">*</span>
              </label>
              <input
                type="text"
                name="ownerName"
                value={credentials.ownerName}
                onChange={handleCredentialChange}
                className="input-field"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Owner Email <span className="text-brand-orange">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={credentials.email}
                onChange={handleCredentialChange}
                className="input-field"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Store Address</label>
              <input
                type="text"
                name="address"
                value={credentials.address}
                onChange={handleCredentialChange}
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Tax ID Number</label>
              <input
                type="text"
                name="taxId"
                value={credentials.taxId}
                onChange={handleCredentialChange}
                className="input-field"
                placeholder="e.g. 12-3456789"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Contact Phone</label>
              <input
                type="text"
                name="contactPhone"
                value={credentials.contactPhone}
                onChange={handlePhoneChange}
                className="input-field"
                placeholder="Contact Phone"
                maxLength={12}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Contact Email</label>
              <input
                type="email"
                name="contactEmail"
                value={credentials.contactEmail}
                onChange={handleCredentialChange}
                className="input-field"
                placeholder="Contact Email"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-6 pt-4 border-t border-gray-100">
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {isSubmitting ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default StoreProfile;
